import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download, Upload, ShieldCheck, AlertTriangle, Loader2, CheckCircle2, FileDown,
  History, Info, Trash2, Clock, Eye,
} from "lucide-react";
import { api, API } from "../lib/api";
import { NightlyRunsReport } from "./NightlyRunsReport";
import { toast } from "sonner";
import { TurkishPasswordWarning } from "./TurkishPasswordWarning";

const GROUP_TR = {
  tasks: "görev", trash: "çöp kutusu", categories: "iş kolu", notes: "not",
  reminders: "hatırlatma", prefs: "tercih", ai: "yapay zeka kaydı",
};

const DAY_SHORT = {
  mon: "PZT", tue: "SAL", wed: "ÇAR", thu: "PER", fri: "CUM", sat: "CMT", sun: "PAZ",
};

const trDateTime = (iso) =>
  new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" });

const humanSize = (n) => {
  if (!n && n !== 0) return "boyut bilinmiyor";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const scopeText = (counts) => {
  const parts = Object.entries(counts || {})
    .filter(([, n]) => n > 0)
    .map(([g, n]) => `${n} ${GROUP_TR[g] || g}`);
  return parts.length ? parts.join(" · ") : "kayıt yok";
};

const backupTitle = (b) => {
  const name = (b.filename || "").replace(/^sertex-yedek-/, "").replace(/\.sertex$/, "");
  return name || b.filename || "yedek";
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const groupByDate = (rows) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const buckets = new Map();
  [...rows]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .forEach((b) => {
      const d = new Date(b.created_at);
      const days = Math.floor((today - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
      let key;
      if (days <= 0) key = "Bugün";
      else if (days === 1) key = "Dün";
      else if (days < 7) key = "Bu hafta";
      else if (days < 30) key = "Bu ay";
      else key = "Daha eski";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(b);
    });
  const order = ["Bugün", "Dün", "Bu hafta", "Bu ay", "Daha eski"];
  return order.filter((k) => buckets.has(k)).map((k) => [k, buckets.get(k)]);
};

const MODES = [
  {
    key: "merge",
    label: "Birleştir",
    desc: "Yedekte olup sistemde bulunmayan kayıtlar eklenir. Mevcut verilerinize hiç dokunulmaz. En güvenli seçenek.",
  },
  {
    key: "smart",
    label: "Akıllı Birleştirme",
    desc: "Aynı görev hem sistemde hem yedekte varsa, yedekteki hâli geçerli olur. Yeni kayıtlar eklenir, hiçbir şey silinmez.",
    note: "Çakışma olursa her zaman YEDEKTEKİ hâli kazanır.",
  },
  {
    key: "replace",
    label: "Üzerine Yaz",
    desc: "Şu andaki tüm aktif görevleriniz çöp kutusuna taşınır, ardından yedekteki görevler yüklenir.",
    note: "Yalnızca GÖREVLER için geçerlidir; diğer veri türleri her zaman birleştirilir. Görevler kalıcı olarak SİLİNMEZ, çöp kutusundan geri alabilirsiniz.",
    danger: true,
  },
];

export const BackupRestorePanel = () => {
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("");
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(["tasks"]);
  const [activeCount, setActiveCount] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [result, setResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [attachInfo, setAttachInfo] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delUser, setDelUser] = useState("");
  const [delPw, setDelPw] = useState("");
  const [withAttach, setWithAttach] = useState(false);       // yedeğe ekleri dahil et
  const [restoreAttach, setRestoreAttach] = useState(false); // VARSAYILAN KAPALI
  const [auto, setAuto] = useState(null);
  const [encrypt, setEncrypt] = useState(false);          // VARSAYILAN KAPALI
  const [encPw, setEncPw] = useState("");
  const [encPw2, setEncPw2] = useState("");
  const [cryptoInfo, setCryptoInfo] = useState(null);
  const [fileEnc, setFileEnc] = useState(null);           // seçilen dosya şifreli mi
  const [backupPw, setBackupPw] = useState("");           // geri yüklerken yedek şifresi
  const [recoverUser, setRecoverUser] = useState("");
  const [recoverPw, setRecoverPw] = useState("");
  const [apprUser, setApprUser] = useState("");
  const [apprPw, setApprPw] = useState("");
  const [foreignFlow, setForeignFlow] = useState("");     // "" | "to_me" | "to_owner"
  const [confirmForeignReplace, setConfirmForeignReplace] = useState(false);
  const [preview, setPreview] = useState(null);           // ÖN İZLEME sonucu
  const [serverPreview, setServerPreview] = useState(null); // sunucu yedeği önizlemesi
  const [nightlyPw, setNightlyPw] = useState("");
  const [nightlyPw2, setNightlyPw2] = useState("");
  const [runs, setRuns] = useState([]);                   // son 7 gece raporu
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [replaceAck, setReplaceAck] = useState(false);    // ÜZERİNE YAZ ek onayı

  const pwStrength = (pw) => {
    let score = 0;
    if (pw.length >= 6) score += 1;
    if (pw.length >= 10) score += 1;
    if (/[A-ZÇĞİÖŞÜ]/.test(pw) && /[a-zçğıöşü]/.test(pw)) score += 1;
    if (/\d/.test(pw)) score += 1;
    if (/[^\w\s]/.test(pw)) score += 1;
    const labels = ["çok zayıf", "zayıf", "orta", "iyi", "güçlü", "çok güçlü"];
    return { score, label: labels[score] };
  };

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/my-backup/list");
      setBackups(Array.isArray(data) ? data : []);
    } catch (e) { /* liste boş kalır */ }
  };

  // Nazik uyarı: en az bir yedek var ama hiçbiri indirilmemişse ve kullanıcı
  // daha önce kapatmadıysa gösterilir.
  const showDownloadNotice =
    !noticeDismissed && backups.length > 0 && !backups.some((b) => b.downloaded);

  const dismissDownloadNotice = async () => {
    setNoticeDismissed(true);
    try {
      await api.post("/my-backup/download-notice/dismiss");
    } catch (e) { /* kapatma yerelde zaten uygulandı */ }
  };

  useEffect(() => {
    loadBackups();
    api.get("/my-backup/scope")
      .then(({ data }) => {
        setGroups(data?.groups || []);
        setPolicy(data?.policy || null);
        setAttachInfo(data?.attachments || null);
      })
      .catch(() => {});
    api.get("/my-backup/tasks/preview")
      .then(({ data }) => setActiveCount(data?.active_tasks ?? null))
      .catch(() => {});
    api.get("/my-backup/auto")
      .then(({ data }) => {
        setAuto(data);
        setNoticeDismissed(!!data?.download_notice_dismissed);
      })
      .catch(() => {});
    api.get("/my-backup/nightly/runs?limit=7")
      .then(({ data }) => setRuns(data?.runs || []))
      .catch(() => {});
    api.get("/my-backup/crypto-info")
      .then(({ data }) => setCryptoInfo(data))
      .catch(() => {});
  }, []);

  const inspectFile = async (f) => {
    setFileEnc(null);
    setBackupPw("");
    setForeignFlow("");
    setApprUser("");
    setApprPw("");
    setConfirmForeignReplace(false);
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/my-backup/inspect", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileEnc(data);
    } catch (e) {
      setFileEnc(null);
    }
  };

  // AŞAMA 6 — yabancı dosya: yetkili onayıyla yükleme
  const handleApprovedRestore = async () => {
    if (!file) return toast.error("Önce bir yedek dosyası seçin.");
    if (!apprUser || !apprPw) return toast.error("Onaylayanın kullanıcı adı ve şifresi gerekli.");
    if (foreignFlow === "to_me" && !password) return toast.error("Kendi şifrenizi de girin.");
    if (fileEnc?.encrypted && !backupPw) return toast.error("Bu dosya şifreli. Yedek alındığında geçerli olan giriş şifrenizi girin.");
    if (!selected.length) return toast.error("En az bir veri türü seçin.");
    if (mode === "replace" && !confirmForeignReplace) {
      setConfirmForeignReplace(true);
      return toast.warning("ÜZERİNE YAZ modu için onay kutusunu işaretleyin.");
    }
    setBusy("approved");
    setResult(null);
    try {
      // 1. ADIM — HEDEF hesabın güvenlik yedeği (fail-closed)
      let tk = ticket?.id;
      if (foreignFlow === "to_owner") {
        const fd0 = new FormData();
        fd0.append("target_user_id", fileEnc.owner_user_id);
        fd0.append("approver_username", apprUser);
        fd0.append("approver_password", apprPw);
        fd0.append("scope", selected.join(","));
        const res0 = await download("/my-backup/restore/prepare-foreign",
          "sertex-hedef-guvenlik-yedegi.sertex", { method: "POST", body: fd0 });
        tk = res0.headers.get("X-Sertex-Restore-Ticket");
        if (!tk) throw new Error("bilet yok");
      }
      if (!tk) {
        toast.error("Önce 1. adımı yapın: güvenlik yedeğini indirin.");
        setBusy("");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("flow", foreignFlow);
      fd.append("approver_username", apprUser);
      fd.append("approver_password", apprPw);
      fd.append("scope", selected.join(","));
      fd.append("ticket", tk);
      if (foreignFlow === "to_me") fd.append("password", password);
      if (foreignFlow === "to_owner") fd.append("target_user_id", fileEnc.owner_user_id);
      if (fileEnc?.encrypted) fd.append("backup_password", backupPw);
      if (restoreAttach) fd.append("attachments", "1");
      if (mode === "replace") fd.append("confirm_replace", "1");
      const { data } = await api.post("/my-backup/restore/approved", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setApprPw("");
      setPassword("");
      setBackupPw("");
      setTicket(null);
      setConfirmForeignReplace(false);
      toast.success(
        data.flow === "to_owner"
          ? `Yedek asıl sahibine (${data.target_username}) geri yüklendi.`
          : `Yedek onayla hesabınıza yüklendi (onaylayan: ${data.approver_username}).`);
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylı yükleme başarısız oldu.");
    } finally { setBusy(""); }
  };

  const saveNightly = async (patch) => {
    setBusy("nightly");
    try {
      const { data } = await api.put("/my-backup/nightly", patch);
      setAuto((prev) => ({ ...prev, ...data }));
      toast.success(
        patch.nightly_enabled === true
          ? "Gece yedeği açıldı. Her gün 03:00'te şifreli yedeğiniz alınacak."
          : patch.nightly_enabled === false
            ? "Gece yedeği kapatıldı. Var olan yedekleriniz silinmedi."
            : `Saklama süresi ${patch.retention_days} gün olarak kaydedildi.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gece yedeği ayarı kaydedilemedi.");
    } finally { setBusy(""); }
  };

  const saveNightlyPassword = async () => {
    if (!nightlyPw) return toast.error("Sisteme giriş şifrenizi yazın.");
    if (nightlyPw !== nightlyPw2) return toast.error("İki alana yazdığınız şifre birbirini tutmuyor.");
    setBusy("nightlypw");
    try {
      const { data } = await api.put("/my-backup/backup-password", { password: nightlyPw });
      setAuto((prev) => ({ ...prev, backup_password: data.backup_password }));
      setNightlyPw("");
      setNightlyPw2("");
      toast.success(data.message || "Giriş şifreniz yedekleme için onaylandı.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şifre onaylanamadı.");
    } finally { setBusy(""); }
  };

  const removeNightlyPassword = async () => {
    setBusy("nightlypw");
    try {
      const { data } = await api.delete("/my-backup/backup-password");
      setAuto((prev) => ({ ...prev, backup_password: data.backup_password, nightly_enabled: false }));
      toast.success(data.message || "Şifre kaydınız kaldırıldı.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şifre kaydı kaldırılamadı.");
    } finally { setBusy(""); }
  };

  const saveAuto = async (patch) => {
    const next = { ...auto, ...patch };
    setAuto(next);
    setBusy("auto");
    try {
      const { data } = await api.put("/my-backup/auto", {
        enabled: next.enabled,
        days: next.days,
        hour: next.hour,
        keep_count: next.keep_count,
        scope: next.scope,
      });
      setAuto({ ...data, weekday_labels: next.weekday_labels, note: next.note });
      toast.success(
        data.pruned_now
          ? `Otomatik yedek ayarları kaydedildi. ${data.pruned_now} eski yedek elendi.`
          : "Otomatik yedek ayarları kaydedildi.");
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ayarlar kaydedilemedi.");
    } finally { setBusy(""); }
  };

  const toggle = (key) => setSelected((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const download = async (url, fallbackName, opts = {}) => {
    const token = localStorage.getItem("sertex_token_v1");
    const res = await fetch(`${API}${url}`, {
      method: opts.method || "GET",
      body: opts.body,
      headers: { Authorization: `Bearer ${token}`, "X-Client-Platform": "web", "X-Sertex-Client": "web" },
    });
    if (!res.ok) throw new Error("indirilemedi");
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = match ? match[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
    return res;
  };

  const handlePrepare = async () => {
    if (!selected.length) return toast.error("En az bir veri türü seçin.");
    setBusy("prepare");
    try {
      const fd = new FormData();
      fd.append("scope", selected.join(","));
      const res = await download("/my-backup/restore/prepare", "sertex-guvenlik-yedegi.sertex",
        { method: "POST", body: fd });
      const id = res.headers.get("X-Sertex-Restore-Ticket");
      const exp = res.headers.get("X-Sertex-Ticket-Expires");
      if (!id) throw new Error("bilet yok");
      setTicket({ id, expires_at: exp });
      toast.success("Güvenlik yedeğiniz bilgisayarınıza indirildi. Şimdi geri yükleyebilirsiniz.");
    } catch (e) {
      setTicket(null);
      toast.error("Güvenlik yedeği alınamadı, bu yüzden geri yükleme başlatılmadı.");
    } finally { setBusy(""); }
  };

  const handleExport = async () => {
    if (!selected.length) return toast.error("En az bir veri türü seçin.");
    if (encrypt) {
      if (encPw.length < 6) return toast.error("Sisteme giriş şifrenizi yazın.");
      if (encPw !== encPw2) return toast.error("İki alana yazdığınız şifre birbirini tutmuyor.");
    }
    setBusy("export");
    try {
      let res;
      if (encrypt) {
        const fd = new FormData();
        fd.append("scope", selected.join(","));
        if (label.trim()) fd.append("label", label.trim());
        if (withAttach) fd.append("attachments", "1");
        fd.append("encrypt", "1");
        fd.append("backup_password", encPw);
        res = await download("/my-backup/export", "sertex-yedek.sertex",
          { method: "POST", body: fd });
      } else {
        const qs = new URLSearchParams({ scope: selected.join(",") });
        if (label.trim()) qs.set("label", label.trim());
        if (withAttach) qs.set("attachments", "1");
        res = await download(`/my-backup/export?${qs.toString()}`, "sertex-yedek.sertex");
      }
      const code = res.headers.get("X-Sertex-Note-Code");
      const limit = res.headers.get("X-Sertex-Limit-MB");
      const size = humanSize(Number(res.headers.get("X-Sertex-Size") || 0));
      if (code === "encrypted" || code === "attachments_encrypted") {
        toast.success(`Şifreli yedek (${size}) bilgisayarınıza indirildi. Şifresini kaybetmeyin.`);
      } else if (code === "attachments_included") {
        toast.success(`Yedek (${size}) ekleriyle birlikte bilgisayarınıza indirildi. Ekli yedekler sunucuda saklanmaz.`);
      } else if (code === "too_large") {
        toast.warning(`Yedek (${size}) bilgisayarınıza indirildi. Sunucuda saklanamayacak kadar büyük (sınır ${limit} MB).`);
      } else if (code === "budget_full") {
        toast.warning("Yedek bilgisayarınıza indirildi; sunucu alanınız dolu olduğu için sunucuda saklanmadı.");
      } else if (code === "store_failed") {
        toast.warning("Yedek bilgisayarınıza indirildi ancak sunucuda saklanamadı.");
      } else {
        toast.success("Yedek dosyanız indirildi ve sunucudaki yedeklerinize eklendi.");
      }
      loadBackups();
    } catch (e) {
      toast.error("Yedek alınamadı. Lütfen tekrar deneyin.");
    } finally { setBusy(""); }
  };

  const handlePreview = async () => {
    if (!file) return toast.error("Önce bir yedek dosyası seçin.");
    if (fileEnc?.encrypted && !backupPw) return toast.error("Bu dosya şifreli. Yedek alındığında geçerli olan giriş şifrenizi girin.");
    if (!selected.length) return toast.error("En az bir veri türü seçin.");
    setBusy("preview");
    setPreview(null);
    setConfirmReplace(false);
    setReplaceAck(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("scope", selected.join(","));
      if (fileEnc?.encrypted) fd.append("backup_password", backupPw);
      if (restoreAttach) fd.append("attachments", "1");
      const { data } = await api.post("/my-backup/restore/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success("Ön izleme hazır. Hiçbir veri değiştirilmedi.");
    } catch (e) {
      setPreview(null);
      toast.error(e?.response?.data?.detail || "Ön izleme yapılamadı.");
    } finally { setBusy(""); }
  };

  // Sunucudaki yedek için ÖNİZLEME (dosya yüklemeden, salt okunur).
  const handlePreviewServer = async (b) => {
    let pw = "";
    if (b.encrypted) {
      pw = window.prompt(
        "Bu yedek şifreli. Önizlemek için bu yedek alındığında geçerli olan giriş şifrenizi girin:", "") || "";
      if (!pw) return toast.error("Şifre girilmediği için önizleme yapılmadı.");
    }
    setBusy(`preview-${b.id}`);
    setServerPreview(null);
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      fd.append("backup_id", b.id);
      fd.append("scope", (b.scope || ["tasks"]).join(","));
      if (pw) fd.append("backup_password", pw);
      const { data } = await api.post("/my-backup/restore/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setServerPreview({ ...data, _row: b });
      toast.success("Ön izleme hazır. Hiçbir veri değiştirilmedi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ön izleme yapılamadı.");
    } finally { setBusy(""); }
  };

  const handleRestore = async () => {
    if (!file) return toast.error("Önce bir yedek dosyası seçin.");
    // Yıkıcı işlem kapısı EN ÖNCE denetlenir; aksi halde kullanıcı eksik onay
    // kutusu yerine "şifrenizi girin" uyarısı görüp yanlış yöne bakıyordu.
    if (!preview) return toast.error("Önce 'ÖNİZLE' butonuna basın: ne olacağını görmeden geri yükleme yapılmaz.");
    if (mode === "replace" && !replaceAck) {
      return toast.error("Yıkıcı işlem için onay kutusunu işaretleyin: silinecek kayıt sayısını onaylamanız gerekiyor.");
    }
    if (!password) return toast.error("Şifrenizi girin.");
    if (fileEnc?.encrypted && !backupPw) return toast.error("Bu dosya şifreli. Yedek alındığında geçerli olan giriş şifrenizi girin.");
    if (!selected.length) return toast.error("En az bir veri türü seçin.");
    if (!ticket) return toast.error("Önce 1. adımı yapın: güvenlik yedeğinizi indirin.");
    if (mode === "replace" && !confirmReplace) return setConfirmReplace(true);
    setBusy("restore");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("password", password);
      fd.append("scope", selected.join(","));
      fd.append("ticket", ticket.id);
      if (fileEnc?.encrypted) fd.append("backup_password", backupPw);
      if (restoreAttach) fd.append("attachments", "1");
      const { data } = await api.post("/my-backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPassword("");
      setBackupPw("");
      setConfirmReplace(false);
      setReplaceAck(false);
      setPreview(null);
      setTicket(null);
      toast.success("Geri yükleme tamamlandı.");
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yükleme başarısız oldu.");
    } finally { setBusy(""); }
  };

  const handleRecover = async () => {
    if (!file) return toast.error("Önce şifreli yedek dosyasını seçin.");
    if (!recoverUser || !recoverPw) return toast.error("Kullanıcı adınızı ve şifrenizi girin.");
    setBusy("recover");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("username", recoverUser);
      fd.append("password", recoverPw);
      await download("/my-backup/recover", "sertex-yedek-kurtarma.sertex",
        { method: "POST", body: fd });
      setRecoverPw("");
      toast.success("Dosya kurtarma anahtarıyla açıldı ve şifresiz hâli bilgisayarınıza indirildi. Bu erişim denetim kaydına yazıldı.");
    } catch (e) {
      toast.error("Dosya kurtarma anahtarıyla açılamadı. Kullanıcı adı/şifrenizi ve dosyayı kontrol edin.");
    } finally { setBusy(""); }
  };

  const handleDelete = async () => {
    if (!delUser || !delPw) return toast.error("Kullanıcı adı ve şifrenizi girin.");
    setBusy("delete");
    try {
      const { data } = await api.delete(`/my-backup/${delTarget.id}`, {
        data: { username: delUser, password: delPw },
      });
      toast.success(data?.message || "Yedeğin içeriği kalıcı olarak yok edildi.");
      setDelTarget(null);
      setDelUser("");
      setDelPw("");
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedek silinemedi.");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="backup-restore-panel">
      <div className="hud-text text-sertex-cyan flex items-center gap-1">
        <ShieldCheck className="h-3 w-3" /> YEDEKLEME VE GERİ YÜKLEME
      </div>
      <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[11px] leading-relaxed">
        Verilerinizi tek bir dosya olarak bilgisayarınıza indirebilir, dilediğiniz
        zaman geri yükleyebilirsiniz. Aşağıdan hangi veri türlerinin yedeğe
        gireceğini seçin. <b>Dosya ekleri</b> yalnızca bilgisayarınıza inen
        yedeklere dahil edilebilir; yapay zeka <b>belge dizini</b> hiçbir yedeğe
        girmez ve geri yüklemeden sonra yeniden oluşturulur.
      </div>

      {/* KAPSAM SEÇİMİ */}
      <div className="border border-sertex-cyan/20 rounded p-3 space-y-2" data-testid="backup-scope">
        <div className="hud-text text-sertex-text">VERİ TÜRLERİ</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {groups.map((g) => (
            <label
              key={g.key}
              data-testid={`backup-group-${g.key}`}
              className={`flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer transition-colors ${
                selected.includes(g.key)
                  ? "border-sertex-cyan/50 bg-sertex-cyan/10"
                  : "border-sertex-cyan/15 hover:border-sertex-cyan/30"
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(g.key)}
                onChange={() => toggle(g.key)}
                data-testid={`backup-group-input-${g.key}`}
              />
              <span className="hud-text text-sertex-text text-[11px] flex-1">
                {g.label.toUpperCase()}
              </span>
              <span className="hud-text text-sertex-textMuted text-[10px]">{g.count}</span>
            </label>
          ))}
          {!groups.length && (
            <div className="hud-text text-sertex-textMuted text-[10px] normal-case">
              Veri türleri yükleniyor…
            </div>
          )}
        </div>
      </div>

      {/* YEDEK AL */}
      <div className="border border-sertex-cyan/20 rounded p-3 bg-sertex-cyan/5 space-y-2">
        <div className="hud-text text-sertex-text">YEDEK AL</div>
        <label
          className="flex items-start gap-2 border border-sertex-cyan/15 rounded px-2 py-1.5 cursor-pointer"
          data-testid="backup-attachments-toggle"
        >
          <input
            type="checkbox"
            checked={withAttach}
            onChange={(e) => setWithAttach(e.target.checked)}
            data-testid="backup-attachments-input"
            className="mt-0.5"
          />
          <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
            <b className="text-sertex-text">Dosya ve fotoğraf eklerini dahil et</b>
            {attachInfo ? ` (${attachInfo.count} ek · ${humanSize(attachInfo.bytes)})` : ""} —
            ekli yedekler yalnızca bilgisayarınıza iner, sunucuda saklanmaz. Yapay zeka
            belge dizini yedeğe girmez; geri yüklemeden sonra arama yeniden indekslenir.
          </span>
        </label>
        <input
          type="text"
          value={label}
          maxLength={48}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Yedek adı (isteğe bağlı) — örn. mart-ayi"
          data-testid="backup-label-input"
          className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
        />

        {/* ŞİFRELEME (AŞAMA 5) — VARSAYILAN KAPALI */}
        <label
          className={`flex items-start gap-2 border rounded px-2 py-1.5 cursor-pointer transition-colors ${
            encrypt ? "border-sertex-cyan/50 bg-sertex-cyan/10" : "border-sertex-cyan/15"
          }`}
          data-testid="backup-encrypt-toggle"
        >
          <input
            type="checkbox"
            checked={encrypt}
            onChange={(e) => { setEncrypt(e.target.checked); setEncPw(""); setEncPw2(""); }}
            data-testid="backup-encrypt-input"
            className="mt-0.5"
          />
          <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
            <b className="text-sertex-text">Bu yedeği şifrele</b> — dosya yalnızca
            belirlediğiniz şifreyle açılır. Şifreleme <b>yalnızca bilgisayarınıza inen</b>{" "}
            yedeklerde uygulanır; sunucuda saklanan yedekler (otomatik yedekler dahil)
            şifrelenmez, erişim denetimiyle korunur.
          </span>
        </label>

        {encrypt && (
          <div className="space-y-2 border border-sertex-cyan/20 rounded p-2" data-testid="backup-encrypt-fields">
            <input
              type="password"
              value={encPw}
              onChange={(e) => setEncPw(e.target.value)}
              placeholder="Sisteme giriş şifreniz"
              data-testid="backup-encrypt-password"
              className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
            />
            <input
              type="password"
              value={encPw2}
              onChange={(e) => setEncPw2(e.target.value)}
              placeholder="Giriş şifreniz (tekrar)"
              data-testid="backup-encrypt-password2"
              className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
            />
            <TurkishPasswordWarning value={`${encPw}${encPw2}`} testId="backup-encrypt-tr-warning" />
            {encPw && (
              <div className="flex items-center gap-2" data-testid="backup-encrypt-strength">
                <div className="flex-1 h-1 bg-black/60 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      pwStrength(encPw).score <= 1 ? "bg-red-400"
                        : pwStrength(encPw).score <= 3 ? "bg-yellow-400" : "bg-green-400"
                    }`}
                    style={{ width: `${(pwStrength(encPw).score / 5) * 100}%` }}
                  />
                </div>
                <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
                  {pwStrength(encPw).label}
                </span>
              </div>
            )}
            {encPw2 && encPw !== encPw2 && (
              <div className="hud-text text-red-300 normal-case tracking-normal text-[10px]" data-testid="backup-encrypt-mismatch">
                Şifreler birbirini tutmuyor.
              </div>
            )}
            <div className="hud-text text-yellow-300/90 normal-case tracking-normal text-[10px] leading-relaxed" data-testid="backup-encrypt-warning">
              Bu şifreyi güvenli bir yerde saklayın. Şifrenizi kaybederseniz dosyayı
              yalnızca <b>yazılım sahibi</b> kurtarma anahtarıyla açabilir; başka bir
              yolu yoktur.{" "}
              {cryptoInfo && !cryptoInfo.recovery_available && (
                <b className="text-red-300">
                  Şu anda bu sunucuda kurtarma anahtarı tanımlı değil; şifrenizi
                  kaybederseniz dosya AÇILAMAZ.
                </b>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
            {selected.length
              ? `${selected.length} veri türü yedeklenecek.`
              : "Hiç veri türü seçilmedi."}
            {policy && (
              <> Sunucuda en fazla <b>{policy.server_keep}</b> yedek ve toplam{" "}
              <b>{policy.budget_mb} MB</b> saklanır; sınır aşılırsa en eski yedek
              silinir. {policy.max_server_backup_mb} MB'tan büyük yedekler yalnızca
              bilgisayarınıza iner.</>
            )}
          </div>
          <button
            onClick={handleExport}
            disabled={busy === "export" || !selected.length}
            data-testid="backup-export-btn"
            className="flex items-center gap-2 px-3 py-2 rounded border border-sertex-cyan/50 bg-sertex-cyan/10 hover:bg-sertex-cyan/20 transition-colors hud-text text-sertex-cyan disabled:opacity-50"
          >
            {busy === "export" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            DOSYAYI İNDİR
          </button>
        </div>
      </div>

      {/* GERİ YÜKLE */}
      <div className="border border-sertex-cyan/20 rounded p-3 space-y-3">
        <div className="hud-text text-sertex-text flex items-center gap-1">
          <Upload className="h-3 w-3" /> GERİ YÜKLE
        </div>
        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
          İki adımlı akış: <b>1)</b> mevcut halinizin güvenlik yedeği bilgisayarınıza iner,
          <b> 2)</b> ardından geri yükleme başlar. Güvenlik yedeği inmeden geri yükleme
          başlamaz. Yukarıda seçtiğiniz veri türleri geri yüklenir; dosyada bulunmayan
          türler atlanır. Eski sürüm (yalnızca görev içeren) yedek dosyaları da yüklenebilir.
        </div>

        <div className="border border-sertex-cyan/20 rounded p-2 space-y-2 bg-sertex-cyan/5">
          <div className="hud-text text-sertex-text text-[11px]">
            1. ADIM · GÜVENLİK YEDEĞİNİ İNDİR
          </div>
          <button
            onClick={handlePrepare}
            disabled={busy === "prepare"}
            data-testid="backup-prepare-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-sertex-cyan/50 bg-sertex-cyan/10 hover:bg-sertex-cyan/20 hud-text text-sertex-cyan disabled:opacity-50 transition-colors"
          >
            {busy === "prepare" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
            MEVCUT HALİMİN YEDEĞİNİ İNDİR
          </button>
          {ticket ? (
            <div
              className="hud-text text-green-300 normal-case tracking-normal text-[10px]"
              data-testid="backup-ticket-ok"
            >
              Güvenlik yedeğiniz indirildi. Geri yükleme izni{" "}
              {new Date(ticket.expires_at).toLocaleTimeString("tr-TR")}{" "}
              saatine kadar geçerli ve tek kullanımlıktır.
            </div>
          ) : (
            <div className="hud-text text-yellow-300/90 normal-case tracking-normal text-[10px]">
              Bu yedek sunucuda saklanmaz, yalnızca size iner. 2. adım bu indirme
              yapılmadan çalışmaz.
            </div>
          )}
        </div>

        <div className="hud-text text-sertex-text text-[11px]">2. ADIM · DOSYAYI YÜKLE</div>

        <label
          className={`flex items-start gap-2 border rounded px-2 py-1.5 cursor-pointer transition-colors ${
            restoreAttach ? "border-yellow-400/50 bg-yellow-500/10" : "border-sertex-cyan/15"
          }`}
          data-testid="restore-attachments-toggle"
        >
          <input
            type="checkbox"
            checked={restoreAttach}
            onChange={(e) => setRestoreAttach(e.target.checked)}
            data-testid="restore-attachments-input"
            className="mt-0.5"
          />
          <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
            <b className="text-sertex-text">Dosya ve fotoğraf eklerini de geri yükle</b>{" "}
            (varsayılan kapalı). Açarsanız ekler sunucu depolama alanınıza yazılır ve{" "}
            <b className="text-yellow-300">bu alan bir daha boşaltılamaz</b>. Aynı içerik
            zaten varsa tekrar yazılmaz, mevcut dosya yeniden kullanılır
            {attachInfo ? ` (bu hesapta şu an ${humanSize(attachInfo.bytes)} ek var)` : ""}.
          </span>
        </label>

        <input
          type="file"
          accept=".sertex"
          data-testid="backup-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setFile(f);
            setResult(null);
            setConfirmReplace(false);
            inspectFile(f);
          }}
          className="w-full text-[11px] text-sertex-textMuted file:mr-2 file:px-2 file:py-1 file:rounded file:border file:border-sertex-cyan/40 file:bg-sertex-cyan/10 file:text-sertex-cyan file:hud-text"
        />

        {fileEnc?.encrypted && (
          <div className="border border-yellow-400/40 bg-yellow-500/10 rounded p-2 space-y-2" data-testid="restore-encrypted-notice">
            <div className="hud-text text-yellow-300 text-[11px] flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> BU DOSYA ŞİFRELİ
            </div>
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
              Geri yüklemek için, bu dosya <b>oluşturulduğu tarihte geçerli olan giriş
              şifrenizi</b> girin. (Giriş şifrenizi sonradan değiştirdiyseniz, eski dosyalar
              eski şifreyle açılır.)
            </div>
            <input
              type="password"
              value={backupPw}
              onChange={(e) => setBackupPw(e.target.value)}
              placeholder="Yedek alındığında geçerli olan giriş şifreniz"
              data-testid="restore-backup-password"
              className="w-full bg-black/40 border border-yellow-400/40 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-yellow-300"
            />
            {fileEnc.can_recover && (
              <div className="border-t border-yellow-400/20 pt-2 space-y-2" data-testid="recovery-box">
                <div className="hud-text text-sertex-cyan text-[10px]">
                  KURTARMA ANAHTARIYLA AÇ (YALNIZCA YAZILIM SAHİBİ)
                </div>
                <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
                  Kullanıcı şifresini bilmeden dosyayı açar ve şifresiz hâlini indirir.
                  Bu erişim <b>denetim kaydına</b> yazılır.
                </div>
                <input
                  type="text"
                  value={recoverUser}
                  onChange={(e) => setRecoverUser(e.target.value)}
                  placeholder="Kullanıcı adınız"
                  data-testid="recovery-username"
                  className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
                />
                <input
                  type="password"
                  value={recoverPw}
                  onChange={(e) => setRecoverPw(e.target.value)}
                  placeholder="Şifreniz"
                  data-testid="recovery-password"
                  className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
                />
                <button
                  onClick={handleRecover}
                  disabled={busy === "recover"}
                  data-testid="recovery-open-btn"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-sertex-cyan/50 bg-sertex-cyan/10 hover:bg-sertex-cyan/20 hud-text text-sertex-cyan disabled:opacity-50 transition-colors"
                >
                  {busy === "recover" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                  KURTARMA ANAHTARIYLA AÇ
                </button>
              </div>
            )}
          </div>
        )}

        {fileEnc && fileEnc.is_mine === false && (
          <div className="border border-orange-400/50 bg-orange-500/10 rounded p-2 space-y-2" data-testid="foreign-file-notice">
            <div className="hud-text text-orange-300 text-[11px] flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> BU DOSYA SİZE AİT DEĞİL
            </div>
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
              Dosyanın sahibi: <b className="text-sertex-text" data-testid="foreign-owner">
                {fileEnc.owner_username || "bilinmiyor"}
              </b>. Normalde bu dosya reddedilir. Devam etmek için bir yetkilinin
              (müdür, yönetici veya süper yönetici) o anda onay vermesi gerekir.
              Yükleme öncesinde hedef hesabın güvenlik yedeği alınır.
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setForeignFlow(foreignFlow === "to_me" ? "" : "to_me")}
                data-testid="foreign-flow-to-me"
                className={`px-2 py-1.5 rounded border hud-text text-[10px] transition-colors ${
                  foreignFlow === "to_me"
                    ? "border-orange-300/70 bg-orange-500/20 text-orange-200"
                    : "border-orange-400/30 text-sertex-textMuted hover:border-orange-300/60"
                }`}
              >
                YETKİLİ ONAYI İLE BANA YÜKLE
              </button>
              {fileEnc.can_restore_to_owner && (
                <button
                  onClick={() => setForeignFlow(foreignFlow === "to_owner" ? "" : "to_owner")}
                  data-testid="foreign-flow-to-owner"
                  className={`px-2 py-1.5 rounded border hud-text text-[10px] transition-colors ${
                    foreignFlow === "to_owner"
                      ? "border-sertex-cyan/70 bg-sertex-cyan/20 text-sertex-cyan"
                      : "border-sertex-cyan/30 text-sertex-textMuted hover:border-sertex-cyan/60"
                  }`}
                >
                  ASIL SAHİBİNE GERİ YÜKLE
                </button>
              )}
            </div>

            {foreignFlow && (
              <div className="space-y-2 border-t border-orange-400/20 pt-2" data-testid="foreign-approval-form">
                {foreignFlow === "to_owner" ? (
                  <div className="hud-text text-sertex-cyan normal-case tracking-normal text-[11px] leading-relaxed" data-testid="foreign-target-banner">
                    Veriler şu hesaba yazılacak:{" "}
                    <b className="text-sertex-text text-sm">{fileEnc.owner_username}</b>
                    <div className="text-sertex-textMuted text-[10px]">
                      Hedef hesabın mevcut hâli, yükleme başlamadan önce güvenlik yedeği
                      olarak bilgisayarınıza iner. Rol, yetki, kota ve şifre alanları
                      hiçbir zaman geri yüklenmez.
                    </div>
                  </div>
                ) : (
                  <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
                    Veriler <b className="text-sertex-text">sizin hesabınıza</b> yazılacak.
                    Onaylayan kişi siz olamazsınız; başka bir yetkilinin kullanıcı adı ve
                    şifresi gerekir. Rol, yetki, kota ve şifre alanları geri yüklenmez.
                  </div>
                )}
                <input
                  type="text"
                  value={apprUser}
                  onChange={(e) => setApprUser(e.target.value)}
                  placeholder="Onaylayan yetkilinin kullanıcı adı"
                  data-testid="approver-username"
                  className="w-full bg-black/40 border border-orange-400/40 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-orange-300"
                />
                <input
                  type="password"
                  value={apprPw}
                  onChange={(e) => setApprPw(e.target.value)}
                  placeholder="Onaylayan yetkilinin şifresi"
                  data-testid="approver-password"
                  className="w-full bg-black/40 border border-orange-400/40 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-orange-300"
                />
                {mode === "replace" && (
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="foreign-replace-confirm">
                    <input
                      type="checkbox"
                      checked={confirmForeignReplace}
                      onChange={(e) => setConfirmForeignReplace(e.target.checked)}
                      data-testid="foreign-replace-confirm-input"
                      className="mt-0.5"
                    />
                    <span className="hud-text text-red-300 normal-case tracking-normal text-[10px] leading-relaxed">
                      ÜZERİNE YAZ onayı: hedef hesabın{" "}
                      {foreignFlow === "to_me" && activeCount !== null
                        ? `${activeCount} aktif görevi`
                        : "tüm aktif görevleri"}{" "}
                      çöp kutusuna taşınacak. Kalıcı silme yoktur, çöp kutusundan geri alınabilir.
                    </span>
                  </label>
                )}
                <button
                  onClick={handleApprovedRestore}
                  disabled={busy === "approved"}
                  data-testid="foreign-approve-btn"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-orange-300/60 bg-orange-500/15 hover:bg-orange-500/25 hud-text text-orange-200 disabled:opacity-50 transition-colors"
                >
                  {busy === "approved" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                  ONAYLA VE YÜKLE
                </button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          {MODES.map((m) => (
            <label
              key={m.key}
              data-testid={`backup-mode-${m.key}`}
              className={`block border rounded p-2 cursor-pointer transition-colors ${
                mode === m.key
                  ? m.danger ? "border-red-400/60 bg-red-500/10" : "border-sertex-cyan/50 bg-sertex-cyan/10"
                  : "border-sertex-cyan/15 hover:border-sertex-cyan/30"
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="restore-mode"
                  checked={mode === m.key}
                  onChange={() => { setMode(m.key); setConfirmReplace(false); setPreview(null); setReplaceAck(false); }}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className={`hud-text ${m.danger ? "text-red-300" : "text-sertex-text"}`}>
                    {m.label.toUpperCase()}
                  </div>
                  <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] mt-0.5 leading-relaxed">
                    {m.desc}
                  </div>
                  {m.note && (
                    <div className="hud-text text-yellow-300/90 normal-case tracking-normal text-[10px] mt-1">
                      {m.note}
                    </div>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>

        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Güvenlik için şifrenizi girin"
          data-testid="backup-password-input"
          className="w-full bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-sertex-cyan"
        />

        <button
          onClick={handlePreview}
          disabled={busy === "preview"}
          data-testid="backup-preview-btn"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-sertex-cyan/40 bg-sertex-cyan/5 hover:bg-sertex-cyan/15 hud-text text-sertex-cyan disabled:opacity-50 transition-colors"
        >
          {busy === "preview" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
          ÖNİZLE — NE OLACAĞINI GÖSTER
        </button>

        {preview && (
          <div
            className="border border-sertex-cyan/30 bg-black/30 rounded p-2.5 space-y-2"
            data-testid="restore-preview-box"
          >
            <div className="hud-text text-sertex-cyan flex items-center gap-1.5">
              <Eye className="h-3 w-3" /> ÖN İZLEME · HİÇBİR VERİ DEĞİŞTİRİLMEDİ
            </div>
            <div
              className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed"
              data-testid="restore-preview-meta"
            >
              Yedek tarihi: <b className="text-sertex-text">
                {preview.backup?.created_at ? trDateTime(preview.backup.created_at) : "bilinmiyor"}
              </b>
              {preview.backup?.label ? <> · Etiket: <b className="text-sertex-text">{preview.backup.label}</b></> : null}
              {" "}· Boyut: <b className="text-sertex-text">{humanSize(preview.backup?.size)}</b>
              {" "}· Sahibi: <b className="text-sertex-text">{preview.backup?.owner_username || "bilinmiyor"}</b>
              {preview.backup?.encrypted ? <> · <span className="text-emerald-300">şifre doğrulandı</span></> : null}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[10px] font-mono" data-testid="restore-preview-table">
                <thead>
                  <tr className="text-sertex-textMuted border-b border-sertex-cyan/20">
                    <th className="text-left py-1 pr-2">VERİ TÜRÜ</th>
                    <th className="text-right px-1">YEDEKTE</th>
                    <th className="text-right px-1">EKLENECEK</th>
                    <th className="text-right px-1">ATLANACAK</th>
                    <th className="text-right px-1">ÇAKIŞMA</th>
                    <th className="text-right pl-1">SİLİNECEK</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.groups || {}).map(([g, v]) => (
                    <tr
                      key={g}
                      className="border-b border-white/5"
                      data-testid={`restore-preview-row-${g}`}
                      title={v.conflict_action}
                    >
                      <td className="text-left py-1 pr-2 text-sertex-text">{v.label}</td>
                      <td className="text-right px-1 tabular-nums">{v.in_file}</td>
                      <td className="text-right px-1 tabular-nums text-emerald-300">{v.add}</td>
                      <td className="text-right px-1 tabular-nums text-sertex-textMuted">{v.skip}</td>
                      <td className="text-right px-1 tabular-nums text-yellow-300">{v.conflict}</td>
                      <td className={`text-right pl-1 tabular-nums ${v.delete ? "text-red-300 font-bold" : "text-sertex-textMuted"}`}>
                        {v.delete}
                      </td>
                    </tr>
                  ))}
                  <tr className="text-sertex-cyan" data-testid="restore-preview-totals">
                    <td className="text-left py-1 pr-2">TOPLAM</td>
                    <td className="text-right px-1 tabular-nums">{preview.totals?.in_file}</td>
                    <td className="text-right px-1 tabular-nums">{preview.totals?.add}</td>
                    <td className="text-right px-1 tabular-nums">{preview.totals?.skip}</td>
                    <td className="text-right px-1 tabular-nums">{preview.totals?.conflict}</td>
                    <td className="text-right pl-1 tabular-nums">{preview.totals?.delete}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
              Çakışmada ne olacak: <b className="text-sertex-text">{preview.groups?.tasks?.conflict_action || "—"}</b>
              {" "}· Ekler: <b className="text-sertex-text">
                {preview.attachments?.count || 0} dosya ({preview.attachments?.mb || 0} MB)
              </b>
            </div>

            {preview.warning && (
              <div
                className="border border-red-400/50 bg-red-500/10 rounded p-2 hud-text text-red-200 normal-case tracking-normal text-[11px] leading-relaxed"
                data-testid="restore-preview-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                {preview.warning}
              </div>
            )}

            {mode === "replace" && (
              <label
                className="flex items-start gap-2 cursor-pointer border border-red-400/40 bg-red-500/5 rounded p-2"
                data-testid="replace-ack-label"
              >
                <input
                  type="checkbox"
                  checked={replaceAck}
                  onChange={(e) => setReplaceAck(e.target.checked)}
                  data-testid="replace-ack-input"
                  className="mt-0.5"
                />
                <span className="hud-text text-red-200 normal-case tracking-normal text-[11px] leading-relaxed">
                  Anladım: <b>{preview.totals?.delete ?? 0} mevcut kayıt</b> çöp kutusuna taşınacak
                  (kalıcı silme değil, geri alınabilir).
                </span>
              </label>
            )}
          </div>
        )}

        <AnimatePresence>
          {confirmReplace && mode === "replace" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              data-testid="backup-replace-confirm"
              className="border border-red-400/50 bg-red-500/10 rounded p-2"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-300 shrink-0 mt-0.5" />
                <div className="hud-text text-red-200 normal-case tracking-normal text-[11px] leading-relaxed">
                  Şu andaki <b>{activeCount ?? "?"} aktif göreviniz çöp kutusuna taşınacak</b> ve
                  yerine yedekteki görevler yüklenecek. Görevler kalıcı olarak silinmez,
                  çöp kutusundan geri alabilirsiniz. İş kollarınız ve diğer veri türleri
                  silinmez, yalnızca eksik olanlar eklenir. Devam etmek için tekrar basın.
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={handleRestore}
          disabled={busy === "restore" || !preview}
          data-testid="backup-restore-btn"
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded border transition-colors hud-text disabled:opacity-50 ${
            mode === "replace"
              ? "border-red-400/50 bg-red-500/10 hover:bg-red-500/20 text-red-200"
              : "border-sertex-cyan/50 bg-sertex-cyan/10 hover:bg-sertex-cyan/20 text-sertex-cyan"
          }`}
        >
          {busy === "restore" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {!preview
            ? "GERİ YÜKLE (önce ÖNİZLE)"
            : confirmReplace && mode === "replace"
              ? "EVET, ÜZERİNE YAZ"
              : "GERİ YÜKLEMEYİ ONAYLA"}
        </button>

        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
          Güvenlik yedeğiniz 1. adımda bilgisayarınıza indi; sunucuda saklanmaz.
          Bir sorun olursa o dosyayla eski halinize dönebilirsiniz.
        </div>
      </div>

      {serverPreview && (
          <div
            className="border border-sertex-cyan/30 bg-black/40 rounded p-2.5 space-y-2 mt-2"
            data-testid="server-preview-box"
          >
            <div className="hud-text text-sertex-cyan flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> SUNUCU YEDEĞİ ÖN İZLEME · VERİ DEĞİŞMEDİ
              </span>
              <button
                onClick={() => setServerPreview(null)}
                data-testid="server-preview-close"
                className="hud-text text-[10px] text-sertex-textMuted hover:text-sertex-cyan"
              >
                KAPAT
              </button>
            </div>
            <div
              className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed"
              data-testid="server-preview-meta"
            >
              Tarih: <b className="text-sertex-text">
                {serverPreview.backup?.created_at ? trDateTime(serverPreview.backup.created_at) : "bilinmiyor"}
              </b>
              {serverPreview.backup?.label ? <> · Etiket: <b className="text-sertex-text">{serverPreview.backup.label}</b></> : null}
              {" "}· Boyut: <b className="text-sertex-text">{humanSize(serverPreview.backup?.size)}</b>
              {" "}· Mod: <b className="text-sertex-text">{(MODES.find((m) => m.key === serverPreview.mode) || {}).label}</b>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] font-mono" data-testid="server-preview-table">
                <thead>
                  <tr className="text-sertex-textMuted border-b border-sertex-cyan/20">
                    <th className="text-left py-1 pr-2">VERİ TÜRÜ</th>
                    <th className="text-right px-1">YEDEKTE</th>
                    <th className="text-right px-1">EKLENECEK</th>
                    <th className="text-right px-1">ATLANACAK</th>
                    <th className="text-right px-1">ÇAKIŞMA</th>
                    <th className="text-right pl-1">SİLİNECEK</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(serverPreview.groups || {}).map(([g, v]) => (
                    <tr key={g} className="border-b border-white/5" title={v.conflict_action}>
                      <td className="text-left py-1 pr-2 text-sertex-text">{v.label}</td>
                      <td className="text-right px-1 tabular-nums">{v.in_file}</td>
                      <td className="text-right px-1 tabular-nums text-emerald-300">{v.add}</td>
                      <td className="text-right px-1 tabular-nums text-sertex-textMuted">{v.skip}</td>
                      <td className="text-right px-1 tabular-nums text-yellow-300">{v.conflict}</td>
                      <td className={`text-right pl-1 tabular-nums ${v.delete ? "text-red-300 font-bold" : "text-sertex-textMuted"}`}>
                        {v.delete}
                      </td>
                    </tr>
                  ))}
                  <tr className="text-sertex-cyan" data-testid="server-preview-totals">
                    <td className="text-left py-1 pr-2">TOPLAM</td>
                    <td className="text-right px-1 tabular-nums">{serverPreview.totals?.in_file}</td>
                    <td className="text-right px-1 tabular-nums">{serverPreview.totals?.add}</td>
                    <td className="text-right px-1 tabular-nums">{serverPreview.totals?.skip}</td>
                    <td className="text-right px-1 tabular-nums">{serverPreview.totals?.conflict}</td>
                    <td className="text-right pl-1 tabular-nums">{serverPreview.totals?.delete}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serverPreview.warning && (
              <div
                className="border border-red-400/50 bg-red-500/10 rounded p-2 hud-text text-red-200 normal-case tracking-normal text-[11px]"
                data-testid="server-preview-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                {serverPreview.warning}
              </div>
            )}
          </div>
        )}

      {/* SONUÇ */}
      {result && (
        <div className="border border-green-400/40 bg-green-500/10 rounded p-3" data-testid="backup-result">
          <div className="hud-text text-green-300 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> GERİ YÜKLEME TAMAMLANDI
          </div>
          <div className="hud-text text-sertex-text normal-case tracking-normal text-[11px] mt-2 space-y-1">
            {Object.entries(result.group_counts || {}).map(([g, c]) => (
              <div key={g} data-testid={`backup-result-${g}`}>
                <b>{result.labels?.[g] || g}</b>: {c.added ? `${c.added} eklendi` : "0 eklendi"}
                {c.updated ? `, ${c.updated} güncellendi` : ""}
                {c.reused ? `, ${c.reused} mevcut kayıt kullanıldı` : ""}
                {c.trashed ? `, ${c.trashed} çöp kutusuna taşındı` : ""}
                {c.skipped ? `, ${c.skipped} atlandı (zaten vardı)` : ""}
              </div>
            ))}
            {!!result.attachments?.in_file && (
              <div data-testid="backup-result-attachments">
                <b>Ekler</b>: {result.attachments.requested
                  ? `${result.attachments.added} yeni yazıldı, ${result.attachments.reused} mevcut ek yeniden kullanıldı`
                  : `dosyada ${result.attachments.in_file} ek var, geri yüklenmedi (seçenek kapalıydı)`}
                {result.attachments.skipped ? `, ${result.attachments.skipped} atlandı` : ""}
              </div>
            )}
            {!!result.attachments?.added && (
              <div className="text-sertex-textMuted">
                Yapay zeka belge dizini yedeğe girmez; aramanın yeniden indekslenmesi
                gerekir.
              </div>
            )}
            {!!result.group_counts?.trash?.skipped && (
              <div className="text-yellow-300/90">
                Çöp kutusundaki {result.group_counts.trash.skipped} kayıt, aynı görev
                sistemde aktif olduğu için eklenmedi — aktif hâli korundu.
              </div>
            )}
          </div>
          {result.safety_backup?.sha256 && (
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] mt-2">
              Güvenlik yedeğiniz 1. adımda bilgisayarınıza indirildi
              ({humanSize(result.safety_backup.size)}).
            </div>
          )}
        </div>
      )}

      {/* GECE YEDEĞİ (opt-in) */}      <div className="border border-emerald-400/25 rounded p-3 space-y-2" data-testid="nightly-backup-section">
        <div className="hud-text text-emerald-300 flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> OTOMATİK GECE YEDEĞİ (03:00)
        </div>

        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
          Her gece saat 03:00'te tüm verileriniz yedeklenir ve <b className="text-sertex-text">sisteme
          giriş şifrenizle şifrelenir</b>. Ayrı bir şifre uydurmanız gerekmez — aşağıya Sertex'e
          girerken kullandığınız şifreyi yazmanız yeterli. İndirdiğiniz .sertex dosyası da aynı
          şifreyle açılır. Dürüst olalım: gece siz yokken şifrelenebilmesi için bu şifre sunucuda
          <b className="text-sertex-text"> şifrelenmiş (sarılmış) hâlde saklanır</b>; düz metin
          olarak hiçbir yere yazılmaz. Dosya ayrıca sistem kurtarma anahtarıyla da açılabilir.
          Varsayılan olarak <b className="text-sertex-text">kapalıdır</b>, yalnızca siz açarsanız çalışır.
        </div>

        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed"
             data-testid="nightly-password-change-note">
          <b className="text-sertex-text">Giriş şifrenizi değiştirirseniz:</b> gece yedeği otomatik
          olarak yeni şifrenize geçer. Ancak <b className="text-sertex-text">eski yedekler,
          oluşturuldukları tarihte geçerli olan şifreyle açılır</b> — şifre değişikliği eski
          dosyaları geriye dönük değiştirmez. Bir aksilik olursa çan bildirimiyle uyarılırsınız.
        </div>

        {auto?.scheduler_running === false && (auto?.nightly_enabled || auto?.enabled) && (
          <div
            className="border border-amber-400/50 bg-amber-500/10 rounded p-2 hud-text text-amber-200 normal-case tracking-normal text-[11px] leading-relaxed"
            data-testid="nightly-scheduler-disabled"
          >
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
            Otomatik yedeği <b>siz açtınız</b>, ancak bu sunucuda otomatik yedek
            zamanlayıcısı kapalı olduğu için gece yedeği <b>alınmıyor</b>. Bu sizin
            hatanız değil; yöneticinizin sunucu ayarını (<b>SERTEX_ENABLE_AUTO_BACKUP</b>)
            açması gerekiyor. O zamana kadar yedeklerinizi elle alabilirsiniz.
          </div>
        )}

        {auto?.recovery_available === false && (          <div
            className="border border-red-400/50 bg-red-500/10 rounded p-2 hud-text text-red-200 normal-case tracking-normal text-[11px] leading-relaxed"
            data-testid="nightly-recovery-missing"
          >
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
            Sunucuda yedek kurtarma anahtarı tanımlı olmadığı için yedek şifreniz güvenli
            biçimde saklanamıyor. Bu yüzden gece yedeği şu anda <b>kullanılamıyor</b>.
            Yöneticinizin sunucu ayarlarını tamamlaması gerekiyor.
          </div>
        )}

        <div className="border border-yellow-400/40 bg-yellow-500/10 rounded p-2 space-y-1.5" data-testid="nightly-password-box">
          <div className="hud-text text-yellow-200 normal-case tracking-normal text-[10px]">
            <Info className="h-3 w-3 inline mr-1 -mt-0.5" />
            Buraya <b>sisteme giriş şifrenizi</b> yazın. Şifre, girişte kullandığınızla
            birebir aynı olmalı; uyuşmazsa kaydedilmez.
          </div>
          {auto?.backup_password?.is_set ? (
            <div className="hud-text text-sertex-text normal-case tracking-normal text-[10px]"
                 data-testid="nightly-password-set">
              Giriş şifreniz yedekleme için kayıtlı: <b>{auto.backup_password.masked}</b>
              {auto.backup_password.set_at ? ` · ${trDateTime(auto.backup_password.set_at)}` : ""}
            </div>
          ) : (
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]"
                 data-testid="nightly-password-unset">
              Gece yedeğini açabilmek için önce sisteme giriş şifrenizi onaylayın.
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <input
              type="password"
              value={nightlyPw}
              onChange={(e) => setNightlyPw(e.target.value)}
              placeholder="Sisteme giriş şifreniz"
              data-testid="nightly-password-input"
              className="flex-1 min-w-[140px] bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1 text-sertex-text text-xs focus:outline-none focus:border-sertex-cyan"
            />
            <input
              type="password"
              value={nightlyPw2}
              onChange={(e) => setNightlyPw2(e.target.value)}
              placeholder="Giriş şifreniz (tekrar)"
              data-testid="nightly-password-input2"
              className="flex-1 min-w-[140px] bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1 text-sertex-text text-xs focus:outline-none focus:border-sertex-cyan"
            />
            <button
              onClick={saveNightlyPassword}
              disabled={busy === "nightlypw" || auto?.recovery_available === false}
              data-testid="nightly-password-save"
              className="px-2 py-1 rounded border border-emerald-400/50 bg-emerald-500/10 hover:bg-emerald-500/20 hud-text text-emerald-200 text-[10px] disabled:opacity-50"
            >
              {auto?.backup_password?.is_set ? "ŞİFREYİ GÜNCELLE" : "ŞİFREYİ ONAYLA"}
            </button>
            {auto?.backup_password?.is_set && (
              <button
                onClick={removeNightlyPassword}
                disabled={busy === "nightlypw"}
                data-testid="nightly-password-remove"
                className="px-2 py-1 rounded border border-red-400/40 text-red-300 hover:bg-red-500/10 hud-text text-[10px] disabled:opacity-50"
              >
                KAYITTAN KALDIR
              </button>
            )}
          </div>
          <TurkishPasswordWarning value={`${nightlyPw}${nightlyPw2}`} testId="nightly-password-tr-warning" />
          <div className="hud-text text-amber-200/90 normal-case tracking-normal text-[10px] leading-relaxed"
               data-testid="nightly-password-tr-note">
            Giriş şifrenizde hâlihazırda Türkçe karakter varsa, şifrenizi Türkçe karakter
            içermeyen bir şifreyle değiştirmeniz daha güvenlidir.
          </div>
        </div>

        <label className="flex items-start gap-2 cursor-pointer" data-testid="nightly-toggle">
          <input
            type="checkbox"
            checked={!!auto?.nightly_enabled}
            onChange={(e) => saveNightly({ nightly_enabled: e.target.checked })}
            disabled={(!auto?.backup_password?.is_set && !auto?.nightly_enabled) || auto?.recovery_available === false}
            data-testid="nightly-enabled-input"
            className="mt-0.5"
          />
          <span className="hud-text text-sertex-text normal-case tracking-normal text-[11px]">
            Gece yedeğini aç (her gün 03:00)
            {!auto?.backup_password?.is_set && !auto?.nightly_enabled && (
              <span className="text-yellow-300"> — önce yedek şifresi tanımlayın</span>
            )}
          </span>
        </label>

        <div className="flex items-center gap-2">
          <span className="hud-text text-sertex-textMuted text-[10px]">SAKLAMA SÜRESİ</span>
          <select
            value={auto?.retention_days ?? 7}
            onChange={(e) => saveNightly({ retention_days: Number(e.target.value) })}
            data-testid="nightly-retention-select"
            className="bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1 text-sertex-text text-xs focus:outline-none focus:border-sertex-cyan"
          >
            {(auto?.retention_day_choices || [7, 14, 30, 90]).map((d) => (
              <option key={d} value={d}>{d} gün</option>
            ))}
          </select>
          <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
            Bu süreyi aşan <b>otomatik</b> yedekler silinir; elle aldığınız yedekler silinmez.
          </span>
        </div>

        {auto?.last_nightly_at && (
          <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]"
               data-testid="nightly-last-run">
            Son gece yedeği: {trDateTime(auto.last_nightly_at)}
            {auto.last_result === "failed" ? " · SONUÇ: BAŞARISIZ" : " · SONUÇ: başarılı"}
          </div>
        )}

        <NightlyRunsReport runs={runs} />
      </div>

      {/* OTOMATİK YEDEK */}
      {auto && (
        <div className="border border-sertex-cyan/20 rounded p-3 space-y-2" data-testid="auto-backup-section">
          <div className="hud-text text-sertex-cyan flex items-center gap-1">
            <Clock className="h-3 w-3" /> OTOMATİK YEDEK
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-backup-toggle">
            <input
              type="checkbox"
              checked={!!auto.enabled}
              onChange={(e) => saveAuto({ enabled: e.target.checked })}
              data-testid="auto-backup-enabled"
            />
            <span className="hud-text text-sertex-text text-[11px]">
              {auto.enabled ? "AÇIK" : "KAPALI"}
            </span>
            <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
              — seçtiğiniz gün ve saatte yedeğiniz kendiliğinden alınır
            </span>
          </label>

          <div className="flex flex-wrap gap-1" data-testid="auto-backup-days">
            {Object.entries(auto.weekday_labels || {}).map(([key, label]) => {
              const on = (auto.days || []).includes(key);
              return (
                <button
                  key={key}
                  onClick={() => saveAuto({
                    days: on ? auto.days.filter((d) => d !== key) : [...(auto.days || []), key],
                  })}
                  data-testid={`auto-day-${key}`}
                  className={`px-2 py-1 rounded border hud-text text-[10px] transition-colors ${
                    on ? "border-sertex-cyan/60 bg-sertex-cyan/15 text-sertex-cyan"
                       : "border-sertex-cyan/15 text-sertex-textMuted hover:border-sertex-cyan/40"
                  }`}
                >
                  {DAY_SHORT[key] || label.slice(0, 3).toUpperCase()}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2">
              <span className="hud-text text-sertex-textMuted text-[10px]">SAAT</span>
              <select
                value={auto.hour ?? 3}
                onChange={(e) => saveAuto({ hour: Number(e.target.value) })}
                data-testid="auto-backup-hour"
                className="bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1 text-sertex-text text-sm"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h} label={`${String(h).padStart(2, "0")}:00`} />
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="hud-text text-sertex-textMuted text-[10px]">SAKLANACAK ADET</span>
              <select
                value={auto.keep_count ?? 3}
                onChange={(e) => saveAuto({ keep_count: Number(e.target.value) })}
                data-testid="auto-backup-keep"
                className="bg-black/40 border border-sertex-cyan/30 rounded px-2 py-1 text-sertex-text text-sm"
              >
                {Array.from({ length: auto.admin_limit || 3 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n} label={String(n)} />
                ))}
              </select>
              <span className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
                (yönetici sınırı: {auto.admin_limit})
              </span>
            </label>
          </div>

          <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed">
            Kapsam: {(auto.scope || []).map((g) => GROUP_TR[g] || g).join(", ")}.{" "}
            {auto.next_run_at ? (
              <span data-testid="auto-next-run">
                Sıradaki otomatik yedek:{" "}
                <b>{trDateTime(auto.next_run_at)}</b> (İstanbul saati).
              </span>
            ) : (
              <span data-testid="auto-next-run">Otomatik yedek şu an planlı değil.</span>
            )}{" "}
            {auto.note}
            {auto.last_result === "failed" && (
              <div className="text-yellow-300/90 mt-1" data-testid="auto-last-error">
                Son otomatik yedek alınamadı: {auto.last_error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* YEDEK GEÇMİŞİ */}
      <div className="border border-sertex-cyan/20 rounded p-3" data-testid="backup-list">
        <div className="hud-text text-sertex-cyan mb-2 flex items-center gap-1">
          <History className="h-3 w-3" /> YEDEK GEÇMİŞİ
        </div>

        {showDownloadNotice && (
          <div
            className="mb-2 border border-yellow-400/35 bg-yellow-500/5 rounded p-2 flex items-start gap-2"
            data-testid="download-notice"
          >
            <Info className="h-3.5 w-3.5 text-yellow-300 mt-0.5 shrink-0" />
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] leading-relaxed min-w-0">
              Yedekleriniz şu anda yalnızca <b className="text-sertex-text">bu sunucuda</b> duruyor.
              Sunucuya bir şey olursa yedek de verinizle birlikte etkilenir; bu yüzden sunucuda
              duran bir kopya tek başına tam bir güvence değildir. Aşağıdaki
              <b className="text-sertex-text"> İNDİR</b> düğmesiyle bir kopyayı kendi cihazınıza
              almanızı öneririz.
            </div>
            <button
              onClick={dismissDownloadNotice}
              data-testid="download-notice-dismiss"
              title="Bu hatırlatmayı bir daha gösterme"
              className="hud-text text-[10px] text-sertex-textMuted hover:text-sertex-cyan shrink-0"
            >
              KAPAT
            </button>
          </div>
        )}

        {!backups.length ? (
          <div
            className="hud-text text-sertex-textMuted normal-case tracking-normal text-[11px] leading-relaxed"
            data-testid="backup-list-empty"
          >
            Henüz hiç yedeğiniz yok. Yukarıdan veri türlerini seçip <b>DOSYAYI İNDİR</b>'e
            bastığınızda ya da bir geri yükleme yaptığınızda (otomatik güvenlik yedeği)
            burada listelenir.
          </div>
        ) : (
          <div className="space-y-3">
            {groupByDate(backups).map(([bucket, rows]) => (
              <div key={bucket} data-testid={`backup-group-bucket-${slug(bucket)}`}>
                <div className="hud-text text-sertex-textMuted text-[10px] border-b border-sertex-cyan/15 pb-1 mb-1.5">
                  {bucket.toUpperCase()} · {rows.length}
                </div>
                <div className="space-y-1.5">
                  {rows.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`backup-row-${b.id}`}
                      className="flex items-start justify-between gap-2 border border-sertex-cyan/10 rounded px-2 py-1.5 hover:border-sertex-cyan/30 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="hud-text text-sertex-text text-[11px] truncate">
                          {backupTitle(b)}
                        </div>
                        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] mt-0.5">
                          {new Date(b.created_at).toLocaleString("tr-TR")} ·{" "}
                          {humanSize(b.size)} ·{" "}
                          {b.kind === "safety" ? "otomatik güvenlik yedeği"
                            : b.kind === "auto" ? "Otomatik" : "Elle"}
                          {b.downloaded === false && b.downloadable !== false ? (
                            <span
                              className="ml-1 text-yellow-300/80"
                              data-testid={`backup-not-downloaded-${b.id}`}
                              title="Bu yedeğin bir kopyasını henüz kendi cihazınıza indirmediniz."
                            >
                              · İndirilmedi
                            </span>
                          ) : null}
                        </div>
                        <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] mt-0.5">
                          {scopeText(b.counts)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handlePreviewServer(b)}
                          disabled={busy === `preview-${b.id}` || b.previewable === false}
                          title="Bu yedeği geri yüklesem ne olur? (salt okunur)"
                          data-testid={`backup-preview-${b.id}`}
                          className="px-2 py-0.5 rounded border border-sertex-cyan/30 hover:bg-sertex-cyan/10 hud-text text-sertex-cyan text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busy === `preview-${b.id}` ? "…" : "ÖNİZLE"}
                        </button>
                        <button
                          onClick={async () => {
                            await download(`/my-backup/${b.id}/download`, b.filename);
                            // İndirildi damgası sunucuda işlendi; listeyi yenile ki
                            // "İndirilmedi" işareti bu satırdan kalksın.
                            loadBackups();
                          }}
                          disabled={b.downloadable === false}
                          title={b.downloadable === false ? "Eski biçim — indirilemez" : ""}
                          data-testid={`backup-download-${b.id}`}
                          className="px-2 py-0.5 rounded border border-sertex-cyan/30 hover:bg-sertex-cyan/10 hud-text text-sertex-cyan text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          İNDİR
                        </button>
                        <button
                          onClick={() => { setDelTarget(b); setDelPw(""); }}
                          data-testid={`backup-delete-${b.id}`}
                          className="px-2 py-0.5 rounded border border-red-400/40 text-red-300 hover:bg-red-500/10 hud-text text-[10px] transition-colors"
                        >
                          SİL
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px] flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              Bir yedeği sildiğinizde sunucudan kalıcı olarak silinir; işlem geri alınamaz.
            </div>
          </div>
        )}
      </div>

      {/* SİLME ONAYI */}
      <AnimatePresence>
        {delTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="backup-delete-confirm"
            className="border border-red-400/50 bg-red-500/10 rounded p-3 space-y-2"
          >
            <div className="hud-text text-red-300 flex items-center gap-1">
              <Trash2 className="h-3 w-3" /> YEDEĞİ SİL
            </div>
            <div className="hud-text text-red-200 normal-case tracking-normal text-[11px] leading-relaxed">
              <b>{backupTitle(delTarget)}</b> ·{" "}
              {new Date(delTarget.created_at).toLocaleString("tr-TR")} ·{" "}
              {humanSize(delTarget.size)}
              <br />
              Bu yedek sunucudan kalıcı olarak silinecek ve bir daha açılamayacak.
              Bu işlem geri alınamaz.
            </div>
            <div className="hud-text text-sertex-textMuted normal-case tracking-normal text-[10px]">
              Yedek sunucudan kalıcı olarak silinecek (meta kayıt ve tüm veri parçaları).
            </div>
            <input
              type="text"
              value={delUser}
              onChange={(e) => setDelUser(e.target.value)}
              placeholder="Kullanıcı adınız"
              data-testid="backup-delete-username"
              className="w-full bg-black/40 border border-red-400/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-red-400"
            />
            <input
              type="password"
              value={delPw}
              onChange={(e) => setDelPw(e.target.value)}
              placeholder="Mevcut şifreniz"
              data-testid="backup-delete-password"
              className="w-full bg-black/40 border border-red-400/30 rounded px-2 py-1.5 text-sertex-text text-sm focus:outline-none focus:border-red-400"
            />
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={busy === "delete"}
                data-testid="backup-delete-submit"
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded border border-red-400/50 bg-red-500/10 hover:bg-red-500/20 hud-text text-red-200 disabled:opacity-50 transition-colors"
              >
                {busy === "delete" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                SUNUCUDAN KALICI SİL
              </button>
              <button
                onClick={() => { setDelTarget(null); setDelPw(""); }}
                data-testid="backup-delete-cancel"
                className="px-3 py-2 rounded border border-sertex-cyan/30 hover:bg-sertex-cyan/10 hud-text text-sertex-textMuted transition-colors"
              >
                VAZGEÇ
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BackupRestorePanel;
